import {VoiceConfig, VoiceIntent, CharacterAsset} from "./voicePlayer.type";
import karangiVoiceConfig from "./config/aespa/voice/karangi.json";
import gelbulnyangiVoiceConfig from "./config/aespa/voice/gelbulnyangi.json";
import mungtaengiVoiceConfig from "./config/aespa/voice/mungtaengi.json";
import rweVoiceConfig from "./config/aespa/voice/rwe.json";
import rizukoVoiceConfig from "./config/riize/rizuko.json";
import songyongdoliVoiceConfig from "./config/riize/songyongdoli.json";
import urakbamVoiceConfig from "./config/riize/urakbam.json";
import tonyangdeokVoiceConfig from "./config/riize/tonyangdeok.json";
import ddolbyeongVoiceConfig from "./config/riize/ddolbyeong.json";
import meongryongieVoiceConfig from "./config/riize/meongryongie.json";

const createPathsFromConfig = (
  config: VoiceConfig,
  basePath: string,
): Record<string, string> => {
  const paths: Record<string, string> = {};

  Object.values(config).forEach((intentConfig) => {
    Object.values(intentConfig).forEach((filenames) => {
      filenames.forEach((filename) => {
        if (!paths[filename]) {
          paths[filename] = `${basePath}/${filename}`;
        }
      });
    });
  });

  return paths;
};

const normalizeIntent = (intent: string): VoiceIntent => {
  if (intent === "normal" || intent === "sensitive") {
    return intent;
  }
  return "normal";
};

const voiceAssetMap: Record<string, Record<string, CharacterAsset>> = {
  aespa: {
    karangi: {
      config: karangiVoiceConfig as unknown as VoiceConfig,
      paths: createPathsFromConfig(
        karangiVoiceConfig as unknown as VoiceConfig,
        "/voices/aespa/karangi",
      ),
    },
    gelbulnyangi: {
      config: gelbulnyangiVoiceConfig as unknown as VoiceConfig,
      paths: createPathsFromConfig(
        gelbulnyangiVoiceConfig as unknown as VoiceConfig,
        "/voices/aespa/gelbulnyangi",
      ),
    },
    mungtaengi: {
      config: mungtaengiVoiceConfig as unknown as VoiceConfig,
      paths: createPathsFromConfig(
        mungtaengiVoiceConfig as unknown as VoiceConfig,
        "/voices/aespa/mungtaengi",
      ),
    },
    rwe: {
      config: rweVoiceConfig as unknown as VoiceConfig,
      paths: createPathsFromConfig(
        rweVoiceConfig as unknown as VoiceConfig,
        "/voices/aespa/rwe",
      ),
    },
  },
  riize: {
    rizuko: {
      config: rizukoVoiceConfig as unknown as VoiceConfig,
      paths: createPathsFromConfig(
        rizukoVoiceConfig as unknown as VoiceConfig,
        "/voices/riize/rizuko",
      ),
    },
    songyongdoli: {
      config: songyongdoliVoiceConfig as unknown as VoiceConfig,
      paths: createPathsFromConfig(
        songyongdoliVoiceConfig as unknown as VoiceConfig,
        "/voices/riize/songyongdoli",
      ),
    },
    urakbam: {
      config: urakbamVoiceConfig as unknown as VoiceConfig,
      paths: createPathsFromConfig(
        urakbamVoiceConfig as unknown as VoiceConfig,
        "/voices/riize/urakbam",
      ),
    },
    tonyangdeok: {
      config: tonyangdeokVoiceConfig as unknown as VoiceConfig,
      paths: createPathsFromConfig(
        tonyangdeokVoiceConfig as unknown as VoiceConfig,
        "/voices/riize/tonyangdeok",
      ),
    },
    ddolbyeong: {
      config: ddolbyeongVoiceConfig as unknown as VoiceConfig,
      paths: createPathsFromConfig(
        ddolbyeongVoiceConfig as unknown as VoiceConfig,
        "/voices/riize/ddolbyeong",
      ),
    },
    meongryongie: {
      config: meongryongieVoiceConfig as unknown as VoiceConfig,
      paths: createPathsFromConfig(
        meongryongieVoiceConfig as unknown as VoiceConfig,
        "/voices/riize/meongryongie",
      ),
    },
  },
};

const getVoiceConfigByCharacter = (
  artist: string,
  character: string,
  intent: string,
): Record<string, string[]> | null => {
  const artistAssets = voiceAssetMap[artist];
  if (!artistAssets) {
    console.warn(`Voice config not found for artist: ${artist}`);
    return null;
  }

  const memberAsset = artistAssets[character];
  if (!memberAsset) {
    console.warn(
      `Voice config not found for artist: ${artist}, character: ${character}`,
    );
    return null;
  }

  const normalizedIntent = normalizeIntent(intent);
  const fullConfig = memberAsset.config;
  const intentConfig: Record<string, string[]> = {};

  Object.keys(fullConfig).forEach((duration) => {
    if (fullConfig[duration] && fullConfig[duration][normalizedIntent]) {
      intentConfig[duration] = fullConfig[duration][normalizedIntent];
    }
  });

  if (Object.keys(intentConfig).length === 0) {
    console.warn(
      `Voice config not found for artist: ${artist}, character: ${character}, intent: ${normalizedIntent}`,
    );
    return null;
  }

  return intentConfig;
};

const getVoicePathByFilename = (
  artist: string,
  character: string,
  filename: string,
): string | null => {
  const artistAssets = voiceAssetMap[artist];
  if (!artistAssets) {
    console.warn(`Voice paths not found for artist: ${artist}`);
    return null;
  }

  const characterAsset = artistAssets[character];
  if (!characterAsset) {
    console.warn(
      `Voice paths not found for artist: ${artist}, character: ${character}`,
    );
    return null;
  }

  const voicePath = characterAsset.paths[filename];
  if (!voicePath) {
    console.warn(`Voice file not found: ${filename}`);
    return null;
  }

  return voicePath;
};

export {getVoiceConfigByCharacter, getVoicePathByFilename};
